import Box from "@material-ui/core/Box";
import {blue, grey} from "@material-ui/core/colors";
import StatusIcon from "@material-ui/icons/RadioButtonUnchecked";
import React from "react";
import {format} from "date-fns";
import {useCurrentShow} from "hooks";
import {getReadableString} from "constraints/general";
import DataRow from "view/components/DataRow";
import ApprovalIcon from "view/images/TaskIcon";
import {makeStyles} from "@material-ui/core/styles";
import HighlightOffIcon from "@material-ui/icons/HighlightOffOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Grid,
  Typography,
  useTheme,
} from "@material-ui/core";
import type {
  MultipleReviewerFormValue,
  MultipleReviewersFormValue,
} from "view/components/TasksList/types";
import {TASK_STATUS_ENUM} from "constraints/taskd";
import Alert from "view/components/Alert";
import {useLocalMapContext} from "view/context/LocalMapContext";
import {T, useTranslate} from "@tolgee/react";
import useDateFnsLocale from "hooks/useDateFnsLocale";

const useStyles = makeStyles((theme) => ({
  fullHeight: {
    height: "100%",
    marginBottom: "1em",
  },
  rightAligned: {
    justifyContent: "flex-start",
    display: "flex",
  },
  centered: {
    justifyContent: "center",
    display: "flex",
  },
  accordionRoot: {
    background: "transparent",
    color: theme.palette.contrastText.main,
    // Drop the Accordion's default `::before` divider so the row keeps a
    // single border like its sibling DataRows.
    "&:before": {
      display: "none",
    },
  },
  // Neutralise the default AccordionSummary spacing so the "Approval" row
  // aligns with the plain DataRows around it.
  summaryRoot: {
    padding: 0,
    minHeight: "auto",
    "&.Mui-expanded": {
      minHeight: "auto",
    },
  },
  summaryContent: {
    margin: 0,
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  boldText: {
    fontSize: "14px",
    fontWeight: 500,
  },
  vline: {
    width: "1px",
    height: "32px",
    background: "#000000",
    opacity: 0.1,
    margin: "1em auto",
  },
}));

export type TaskMultipleReviewersApprovalDropdownProps = {
  multipleReviewers: MultipleReviewersFormValue;
  taskStatus?: string;
  showOrganiserList: any;
  propsClasses?: Record<string, string | number>;
  isReviewStatus?: boolean;
};

export const getInitials = (nameString = "") => {
  const names = nameString.trim().split(/\s+/);
  const initials = names
    .map((name) => (name ? name[0].toUpperCase() : ""))
    .join("");
  return initials.length > 2 ? initials.substr(0, 2) : initials;
};

export const getAvatar = (
  reviewer: MultipleReviewerFormValue,
  organiser: {label: string},
  userLabel = "User"
) => {
  return (
    <Avatar variant="circle" style={{background: blue.A100, color: blue[900]}}>
      {getInitials(reviewer.type === "reviewer" ? organiser?.label : userLabel)}
    </Avatar>
  );
};

const TaskMultipleReviewersApprovalDropdown: React.FC<TaskMultipleReviewersApprovalDropdownProps> = ({
  multipleReviewers,
  showOrganiserList,
  taskStatus,
  propsClasses,
  isReviewStatus,
}) => {
  const [expanded, setExpanded] = React.useState<string | false>("");
  const classes = useStyles();
  const theme = useTheme();
  const {t} = useTranslate();
  const {organiser: localizedOrganiser} = useLocalMapContext();
  const handleChange = (panel: string) => (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setExpanded(newExpanded ? panel : false);
  };
  const [show] = useCurrentShow();
  const locale = useDateFnsLocale();
  const dateFormat = `${getReadableString(show)}, HH:mm`;
  const userLabel = t("common.tasks.multi_reviewer.user");
  const getApprovalText = (
    reviewer: MultipleReviewerFormValue,
    isSequential: boolean,
    prevApproved: boolean
  ) => {
    const isRevision = taskStatus === TASK_STATUS_ENUM.revision;
    if (isSequential) {
      if (prevApproved) {
        if (taskStatus === "To Do") {
          return (
            <Typography className={classes.boldText}>
              {t("common.tasks.multi_reviewer.waiting_for_submission")}
            </Typography>
          );
        }
        if (isRevision) {
          return (
            <Typography className={classes.boldText}>
              <T keyName="common.tasks.status.revision_required" />
            </Typography>
          );
        }
        if (reviewer.isRejected) {
          return (
            <Typography className={classes.boldText}>
              {t("common.tasks.multi_reviewer.rejected")}
            </Typography>
          );
        }
        if (!reviewer.isApproved) {
          return (
            <Typography className={classes.boldText}>
              {t("common.tasks.multi_reviewer.pending")}
            </Typography>
          );
        }
      }
      if (!prevApproved) {
        if (!reviewer.isApproved) {
          return (
            <Typography className={classes.boldText}>
              {t("common.tasks.multi_reviewer.in_queue")}
            </Typography>
          );
        }
      }
    }
    if (taskStatus === "To Do") {
      return (
        <Typography className={classes.boldText}>
          {t("common.tasks.multi_reviewer.waiting_for_submission")}
        </Typography>
      );
    }
    if (isRevision) {
      return (
        <Typography className={classes.boldText}>
          <T keyName="common.tasks.status.revision_required" />
        </Typography>
      );
    }
    if (reviewer.isRejected) {
      return (
        <Typography className={classes.boldText}>
          {t("common.tasks.multi_reviewer.rejected")}
        </Typography>
      );
    }
    if (!reviewer.isApproved) {
      return (
        <Typography className={classes.boldText}>
          {t("common.tasks.multi_reviewer.pending")}
        </Typography>
      );
    }
    if (reviewer.isApproved) {
      return (
        <Typography className={classes.boldText}>
          {t("common.tasks.multi_reviewer.approved")}
        </Typography>
      );
    }
  };

  const getApprovedCount = () => {
    let count = 0;
    if (multipleReviewers?.values) {
      for (const value of multipleReviewers?.values) {
        if (value && value?.isApproved) {
          count += 1;
        }
      }
    }

    return count;
  };
  const getApprovalIcon = (
    reviewer: MultipleReviewerFormValue,
    isSequential: boolean,
    prevApproved: boolean
  ) => {
    if (isSequential) {
      if (prevApproved) {
        if (taskStatus === "To Do") {
          return <StatusIcon style={{color: grey[500]}} />;
        }
        if (reviewer.isRejected) {
          return <HighlightOffIcon style={{color: theme.palette.error.main}} />;
        }
        if (!reviewer.isApproved) {
          return (
            <CheckCircleOutlineIcon
              style={{color: theme.palette.warning.main}}
            />
          );
        }
      }
      if (!prevApproved) {
        if (!reviewer.isApproved) {
          return <StatusIcon style={{color: grey[500]}} />;
        }
      }
    }

    if (taskStatus === "To Do") {
      return <StatusIcon style={{color: grey[500]}} />;
    }
    if (reviewer.isRejected) {
      return <HighlightOffIcon style={{color: theme.palette.error.main}} />;
    }
    if (!reviewer.isApproved) {
      return (
        <CheckCircleOutlineIcon style={{color: theme.palette.warning.main}} />
      );
    }
    if (reviewer.isApproved) {
      return (
        <CheckCircleOutlineIcon style={{color: theme.palette.success.main}} />
      );
    }
  };

  const getTimestampMs = (t?: Date | string | null): number | null => {
    if (!t) {
      return null;
    }
    const ms = new Date(t).getTime();
    return Number.isFinite(ms) ? ms : null;
  };

  const getReviewTime = (reviewer: MultipleReviewerFormValue) => {
    if (reviewer) {
      const {approvalDate, rejectionDate, isRejected} = reviewer;
      const approvalMs = getTimestampMs(approvalDate);
      const rejectionMs = getTimestampMs(rejectionDate);
      if (approvalMs === null && rejectionMs === null) {
        return "";
      }

      const ms = isRejected ? rejectionMs : approvalMs;
      if (ms === null) {
        return "";
      }
      const date = new Date(ms);
      return format(date, dateFormat, {locale});
    }
    return "";
  };
  const renderReviewers = () => {
    return multipleReviewers?.values?.map(
      (reviewer: MultipleReviewerFormValue, index: number) => {
        const organiser = showOrganiserList.find(
          (org: {value: string}) => org.value === reviewer.value
        );
        const prevApproved =
          index === 0
            ? true
            : multipleReviewers?.values?.[index - 1]?.isApproved;
        return (
          <Grid
            container
            className={classes.fullHeight}
            key={`mr-dropdown-${index}`}
          >
            {/* Row 1 */}
            <Grid
              item
              xs={2}
              container
              justify="center"
              alignItems="center"
              className={classes.centered}
            >
              {getApprovalIcon(
                reviewer,
                multipleReviewers?.isSequential as boolean,
                prevApproved || false
              )}
            </Grid>
            <Grid
              item
              xs={6}
              container
              justify="flex-start"
              alignItems="center"
              className={classes.rightAligned}
            >
              {getApprovalText(
                reviewer,
                multipleReviewers?.isSequential as boolean,
                prevApproved || false
              )}
            </Grid>
            <Grid item xs={4} container alignItems="center" />

            {/* Row 2 */}
            <Grid
              item
              xs={2}
              container
              justify="center"
              alignItems="center"
              className={classes.centered}
            >
              <div className={classes.vline} />
            </Grid>
            <Grid
              item
              xs={6}
              container
              justify="flex-start"
              alignItems="center"
              className={classes.rightAligned}
            >
              <Box style={{display: "flex", flexDirection: "row"}}>
                <Box>{getAvatar(reviewer, organiser, userLabel)}</Box>
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    marginLeft: "1em",
                  }}
                >
                  <Box className={classes.boldText}>
                    {reviewer.type === "reviewer"
                      ? `${organiser?.label || ""} ${organiser?.company || ""}`
                      : userLabel}
                  </Box>
                  <Box
                    style={{
                      fontSize: "12px",
                      fontWeight: 400,
                      color: grey[500],
                    }}
                  >
                    {reviewer.type === "reviewer"
                      ? localizedOrganiser
                      : t("common.tasks.multi_reviewer.by_value", {
                          value: reviewer.value,
                        })}
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              xs={4}
              container
              justify="center"
              alignItems="center"
              className={classes.centered}
              style={{fontSize: "12px", fontWeight: 400, color: grey[500]}}
            >
              {getReviewTime(reviewer)}
            </Grid>
          </Grid>
        );
      }
    );
  };
  return (
    <Accordion
      expanded={expanded === "reviewers"}
      onChange={handleChange("reviewers") as any}
      className={classes.accordionRoot}
      style={{boxShadow: "none", margin: 0}}
    >
      <AccordionSummary
        aria-controls="reviewers-content"
        id="reviewers-header"
        classes={{root: classes.summaryRoot, content: classes.summaryContent}}
        style={{boxShadow: "none"}}
      >
        <DataRow
          label={t("common.tasks.approval")}
          value={t("common.tasks.multi_reviewer.approved_count", {
            count: getApprovedCount(),
            total: multipleReviewers?.values
              ? multipleReviewers?.values.length
              : 0,
          })}
          classes={isReviewStatus ? propsClasses : undefined}
          icon={<ApprovalIcon />}
          customWidth="100%"
        />
      </AccordionSummary>
      <AccordionDetails style={{display: "flex", flexDirection: "column"}}>
        {renderReviewers()}
        <Alert severity="info">
          <T keyName="common.tasks.multi_reviewer.rejection_notice" />
        </Alert>
      </AccordionDetails>
    </Accordion>
  );
};

export default TaskMultipleReviewersApprovalDropdown;
